'use client'

import { cn } from '@/lib/utils'
import type { ImageStatus, ImageSortOption } from '@/types/image'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Dropdown/Select'
import { Tooltip } from '@/components/ui/Tooltip'

interface FilterPillsProps {
  /** Current filter status */
  status: ImageStatus
  /** Called when filter changes */
  onStatusChange: (status: ImageStatus) => void
  /** Current sort option */
  sort: ImageSortOption
  /** Called when sort changes */
  onSortChange: (sort: ImageSortOption) => void
  /** Count of images per status (optional) */
  counts?: {
    all: number
    original: number
    edited: number
    favorites: number
  }
  /** Whether to show the favorites filter (authenticated users only) */
  showFavorites?: boolean
  /** Hide the sort dropdown (when handled externally) */
  hideSort?: boolean
}

const STATUS_OPTIONS: { value: ImageStatus; label: string; tooltip: string }[] =
  [
    { value: 'all', label: 'All', tooltip: 'View all images' },
    {
      value: 'original',
      label: 'Original',
      tooltip: 'View original uploaded images',
    },
    { value: 'edited', label: 'Edited', tooltip: 'View edited images' },
    {
      value: 'favorites',
      label: 'Favorites',
      tooltip: 'View your favorite images',
    },
  ]

const SORT_OPTIONS: { value: ImageSortOption; label: string }[] = [
  { value: 'recent', label: 'Recent' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'name', label: 'Name' },
]

/**
 * FilterPills — status filter pills + sort dropdown for the image library.
 * Counts are hidden on mobile to keep pills compact; horizontal scroll handles overflow.
 */
export function FilterPills({
  status,
  onStatusChange,
  sort,
  onSortChange,
  counts,
  hideSort = false,
  showFavorites = false,
}: FilterPillsProps) {
  // Filter out favorites option if not authenticated
  const visibleOptions = showFavorites
    ? STATUS_OPTIONS
    : STATUS_OPTIONS.filter((opt) => opt.value !== 'favorites')
  return (
    <div className="flex flex-wrap items-center gap-3 sm:gap-4">
      {!hideSort && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground hidden sm:inline">
            Sort:
          </span>
          <Select value={sort} onValueChange={onSortChange}>
            <SelectTrigger className="w-[100px] h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Filter pills — horizontal scroll on mobile so they never wrap or truncate */}
      <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none flex-nowrap min-w-0">
        {visibleOptions.map((option) => {
          const isActive = status === option.value
          const count = counts?.[option.value]

          return (
            <Tooltip key={option.value} content={option.tooltip} side="bottom">
              <button
                onClick={() => onStatusChange(option.value)}
                className={cn(
                  'shrink-0 rounded-full font-medium whitespace-nowrap',
                  'px-2.5 py-1 text-xs sm:px-3 sm:py-1.5 sm:text-sm',
                  'transition-all duration-200 touch-manipulation',
                  'border',
                  isActive
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-muted-foreground border-border hover:border-primary/50 hover:text-foreground'
                )}
              >
                {option.label}
                {count !== undefined && count > 0 && (
                  <span
                    className={cn(
                      'ml-1 text-[11px] hidden sm:inline',
                      isActive ? 'opacity-80' : 'opacity-60'
                    )}
                  >
                    ({count})
                  </span>
                )}
              </button>
            </Tooltip>
          )
        })}
      </div>
    </div>
  )
}
